import React, { useState, useEffect, useRef } from 'react';
import {
  TextInput,
  Textarea,
  HelperText,
  Spinner,
  Datepicker
} from "flowbite-react";
import { db, uploadProfilePhoto, uploadCertificate } from "../firebase";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { Plus, X, AlertTriangle, Copy, Check } from 'lucide-react';
import { therapistReviewUrl } from '../utils/reviewLink';
import { acceptsPublicReviews } from '../utils/reviewEligibility';
import FileUpload from './FileUpload';
import { TherapistProfileProps, CertificateEntry } from '../types/therapist';
import { offerFromVitrineFlags, priceForConstellationOffer, vitrineFlagsFromOffer, formatConstellationPrice } from '../constants/planCommerce';
import { useConstellationPlanPrices } from '../hooks/useConstellationPlanPrices';

function TherapistProfile({ user }: TherapistProfileProps) {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [bio, setBio] = useState("");
  const [inSocialVitrine, setInSocialVitrine] = useState(false);
  const [inPlatformVitrine, setInPlatformVitrine] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [profilePhotoURL, setProfilePhotoURL] = useState<string | null>(null);

  const [certificates, setCertificates] = useState<CertificateEntry[]>([]);
  const certificateIdCounter = useRef(0);

  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const planPrices = useConstellationPlanPrices();
  const [isLoading, setIsLoading] = useState(true);
  const [reviewLinkCopied, setReviewLinkCopied] = useState(false);
  const [canReceiveReviews, setCanReceiveReviews] = useState(false);

  const fetchProfileData = async () => {
    if (!user?.uid) {
      setIsLoading(false);
      setFormMessage("Erro: Usuário não autenticado.");
      return;
    }

    try {
      setIsLoading(true);
      console.log("Buscando dados do perfil...");
      const therapistRef = doc(db, 'therapists', user.uid);
      const docSnap = await getDoc(therapistRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        // MUDANÇA AQUI: Lendo os campos em inglês do Firestore
        setName(data.nomePublico || "");
        setTitle(data.title || "");
        setBio(data.bio || "");
        const flags = vitrineFlagsFromOffer(data);
        setCanReceiveReviews(acceptsPublicReviews({
          showInShowcase: data.showInShowcase,
          nomePublico: data.nomePublico,
        }));
        setInSocialVitrine(flags.social);
        setInPlatformVitrine(flags.platform);
        setProfilePhotoURL(data.profilePhotoURL || null);

        // MUDANÇA AQUI: Lendo os campos em inglês do Firestore
        if (data.certificates && Array.isArray(data.certificates)) {
          let counter = 0;
          const loadedCertificates = data.certificates.map((cert: any) => ({
            id: counter++,
            institution: cert.institution || "",
            name: cert.name || "",
            graduationDate: cert.graduationDate?.toDate
              ? cert.graduationDate.toDate()
              : (cert.graduationDate ? new Date(cert.graduationDate) : null),
            file: null,
            certificateURL: cert.certificateURL || null
          }));
          setCertificates(loadedCertificates);
          certificateIdCounter.current = counter;
        }
      } else {
        console.log("Nenhum perfil encontrado, usuário novo.");
      }
    } catch (error: any) {
      console.error("Erro ao carregar perfil:", error);
      setFormMessage(`Erro ao carregar dados: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [user]);

  const handleAddCertificateRow = () => {
    const newId = certificateIdCounter.current++;
    setCertificates(prev => [
      ...prev,
      { id: newId, institution: "", name: "", graduationDate: null, file: null, certificateURL: null }
    ]);
  };

  const handleCertificateChange = (
    id: number,
    field: 'institution' | 'name' | 'graduationDate' | 'file',
    value: string | File | Date | null
  ) => {
    setCertificates(prev =>
      prev.map(cert =>
        cert.id === id ? { ...cert, [field]: value } : cert
      )
    );
  };

  const handleRemoveCertificateRow = (id: number) => {
    setCertificates(prev => prev.filter(cert => cert.id !== id));
  };

  const handleProfilePhotoChange = (file: File | null) => {
    setProfilePhoto(file);
    // Se o usuário removeu o arquivo e não há arquivo novo, limpar a URL existente
    if (file === null) {
      setProfilePhotoURL(null);
    }
  };

  const handleCopyReviewLink = async () => {
    if (!user?.uid) return;
    try {
      await navigator.clipboard.writeText(therapistReviewUrl(user.uid));
      setReviewLinkCopied(true);
      window.setTimeout(() => setReviewLinkCopied(false), 2500);
    } catch {
      setFormMessage("Não foi possível copiar o link. Copie manualmente.");
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!user?.uid) {
      setFormMessage("Erro: Usuário não autenticado. Não é possível salvar.");
      return;
    }

    setIsSubmitting(true);
    setFormMessage("Salvando...");

    try {
      let newProfilePhotoURL = profilePhotoURL;

      // Upload da foto de perfil se houver uma nova
      if (profilePhoto) {
        setFormMessage("Enviando foto de perfil...");
        try {
          newProfilePhotoURL = await uploadProfilePhoto(user.uid, profilePhoto);
          setProfilePhotoURL(newProfilePhotoURL);
        } catch (uploadError: any) {
          throw new Error(`Erro no upload da foto: ${uploadError.message}`);
        }
      }

      // Upload dos certificados
      const certificatesToSave = await Promise.all(
        certificates.map(async (cert, index) => {
          let certificateURL = cert.certificateURL;
          
          if (cert.file) {
            setFormMessage(`Enviando certificado ${index + 1}...`);
            try {
              certificateURL = await uploadCertificate(user.uid, `cert-${cert.id}`, cert.file);
            } catch (uploadError: any) {
              throw new Error(`Erro no upload do certificado ${cert.name}: ${uploadError.message}`);
            }
          }

          return {
            institution: cert.institution,
            name: cert.name,
            graduationDate: cert.graduationDate,
            certificateURL: certificateURL
          };
        })
      );

      setFormMessage("Salvando dados...");

      const constellationOffer = offerFromVitrineFlags(inSocialVitrine, inPlatformVitrine);
      const parsedPrice = priceForConstellationOffer(constellationOffer);

      const dataToSave = {
        nomePublico: name,
        title: title,
        bio: bio,
        constellationOffer: constellationOffer || null,
        constellationPrice: parsedPrice,
        profilePhotoURL: newProfilePhotoURL,
        certificates: certificatesToSave,
        updatedAt: new Date()
      };

      const therapistRef = doc(db, 'therapists', user.uid);
      
      try {
        // Tenta fazer update primeiro
        await updateDoc(therapistRef, dataToSave);
      } catch (updateError: any) {
        // Se o documento não existir, cria um novo
        if (updateError.code === 'not-found') {
          await setDoc(therapistRef, dataToSave);
        } else {
          throw updateError;
        }
      }

      setFormMessage("Perfil salvo com sucesso!");
      
      // Limpar arquivos após upload bem-sucedido
      setProfilePhoto(null);
      setCertificates(prev => prev.map(cert => ({ ...cert, file: null })));
      // Recarregar dados do perfil após salvar
      await fetchProfileData();

      setTimeout(() => {
        setFormMessage(null);
      }, 3000);

    } catch (error: any) {
      console.error("Erro ao salvar perfil:", error);
      setFormMessage(`Erro: ${error.message || 'Ocorreu um erro desconhecido.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-10">
        <Spinner aria-label="Carregando perfil..." size="xl" />
        <span className="pl-3 text-lg">Carregando seu perfil...</span>
      </div>
    );
  }

  // O JSX abaixo permanece 100% em Português
  return (
    <div>
      <h4 className='text-xl font-semibold text-gray-800 mb-6'>Editar Perfil de Terapeuta</h4>

      <form className="flex flex-col gap-6 max-w-6xl mx-auto" onSubmit={handleSubmit}>
        <div className="mb-2 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
          <div className="flex"><div className="flex-shrink-0"><AlertTriangle className="h-5 w-5 text-yellow-500" /></div><div className="ml-3"><p className="text-sm text-yellow-800">Este será seu perfil público.</p></div></div>
        </div>

        {user?.uid && canReceiveReviews && (
          <div className="rounded-xl border border-[#D5E1DB] bg-[#F7FBF9] p-4">
            <p className="text-sm font-medium text-[#2E4B4C]">Link para avaliação</p>
            <p className="mt-1 text-sm text-[#5A7272]">
              Envie para quem você atendeu — mesmo fora da plataforma. Uma avaliação por pessoa.
            </p>
            <p className="mt-2 break-all text-xs text-[#4A7C75]">{therapistReviewUrl(user.uid)}</p>
            <button
              type="button"
              onClick={handleCopyReviewLink}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#4A7C75] px-4 py-2 text-sm font-semibold text-white hover:bg-[#406b65]"
            >
              {reviewLinkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {reviewLinkCopied ? "Link copiado" : "Copiar link de avaliação"}
            </button>
          </div>
        )}
        {user?.uid && !canReceiveReviews && !isLoading && (
          <div className="rounded-xl border border-[#D5E1DB] bg-[#F7FBF9] p-4">
            <p className="text-sm font-medium text-[#2E4B4C]">Link para avaliação</p>
            <p className="mt-1 text-sm text-[#5A7272]">
              O link fica disponível quando o perfil está na vitrine.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

          <div className="md:col-span-1 flex flex-col gap-6">
            <div>
              <div className="mb-2 block ">
                <h3 className='text-lg font-medium text-gray-800 mb-4'>Seu Nome</h3>
              </div>
              <TextInput
                id="name"
                type="text"
                placeholder="Seu nome"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <div className="mb-2 block ">
                <h3 className='text-lg font-medium text-gray-800 mb-4'>Seu Título</h3>
              </div>
              <TextInput
                id="title"
                type="text"
                placeholder="Mestre em Terapia Constelar"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className=''>
              <div className="mb-2 block ">
                <h3 className='text-lg font-medium text-gray-800 mb-4'>Sua Bio</h3>
              </div>
              <Textarea
                className="border-t border-gray-300"
                id='bio'
                placeholder="Descreva um pouco da sua trajetória..."
                required
                rows={4}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
              />
            </div>

            <div>
              <div className="mb-2 block ">
                <h3 className='text-lg font-medium text-gray-800 mb-1'>Onde você aparece na vitrine</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Pode marcar os dois se quiser aparecer nas duas vitrines. Projeto social a {formatConstellationPrice(planPrices.social)} e constelação da plataforma a {formatConstellationPrice(planPrices.platform)}.
                </p>
              </div>
              <div className="space-y-2">
                <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${inSocialVitrine ? "border-[#4A7C75] bg-[#F7FBF9]" : "border-gray-200"}`}>
                  <input
                    type="checkbox"
                    name="constellationOfferSocial"
                    className="mt-1"
                    checked={inSocialVitrine}
                    onChange={() => setInSocialVitrine((current) => !current)}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-800">Projeto social — {formatConstellationPrice(planPrices.social)}</span>
                    <span className="block text-xs text-gray-500">Aparece na vitrine do projeto social.</span>
                  </span>
                </label>
                <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${inPlatformVitrine ? "border-[#A56096] bg-[#FDF8FC]" : "border-gray-200"}`}>
                  <input
                    type="checkbox"
                    name="constellationOfferPlatform"
                    className="mt-1"
                    checked={inPlatformVitrine}
                    onChange={() => setInPlatformVitrine((current) => !current)}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-800">Constelação da plataforma — {formatConstellationPrice(planPrices.platform)}</span>
                    <span className="block text-xs text-gray-500">Aparece na vitrine de venda de constelação.</span>
                  </span>
                </label>
              </div>
            </div>

            <div className="mb-2 block ">
              <h3 className='text-lg font-medium text-gray-800 mb-4'>Foto de Perfil</h3>
              <FileUpload
                id="profile-photo-input"
                accept="image/*"
                onFileChange={handleProfilePhotoChange}
                showPreview={true}
                previewHeight="h-48"
                existingImageUrl={profilePhotoURL}
                placeholder={{
                  title: profilePhotoURL && !profilePhoto ? "Clique para alterar" : "Clique para enviar",
                  subtitle: "PNG, JPG ou JPEG (MAX. 5MB)"
                }}
              />
            </div>
          </div>

          <div className="md:col-span-2 flex flex-col gap-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className='text-lg font-medium text-gray-800'>Formações e Certificados</h3>
              <button
                type="button"
                className="px-3 py-1 text-blue-600 border border-blue-300 rounded text-sm hover:bg-blue-50 flex items-center gap-1"
                onClick={handleAddCertificateRow}
              >
                <Plus className="w-3 h-3" />Adicionar
              </button>
            </div>

            <div className="space-y-4">
              {certificates.map((certificate, index) => (
                <div
                  key={certificate.id}
                  className="flex items-start gap-2 p-4 border rounded-lg bg-gray-50"
                >
                  <div className="flex-1 space-y-3">

                    <h4 className="text-md font-semibold text-gray-700 border-b pb-2 mb-4">
                      Formação #{index + 1}
                    </h4>

                    <h3 className='text-lg font-medium text-gray-800 mb-4'>Instituição de Ensino</h3>
                    <TextInput
                      id={`cert-institution-${certificate.id}`}
                      type="text"
                      required
                      value={certificate.institution}
                      onChange={(e) =>
                        handleCertificateChange(certificate.id, 'institution', e.target.value)
                      }
                    />

                    <h3 className='text-lg font-medium text-gray-800 mb-4'>Nome da formação</h3>
                    <TextInput
                      id={`cert-name-${certificate.id}`}
                      type="text"
                      required
                      value={certificate.name}
                      onChange={(e) =>
                        handleCertificateChange(certificate.id, 'name', e.target.value)
                      }
                    />

                    <h3 className='text-lg font-medium text-gray-800 mb-4'>Data de Formação</h3>
                    <Datepicker
                      title="Escolher data de formação"
                      language="pt-BR" // Mantido em PT-BR
                      value={certificate.graduationDate || null}
                      onChange={(date: Date | null) =>
                        handleCertificateChange(certificate.id, 'graduationDate', date)
                      }
                    />

                    <h3 className='text-lg font-medium text-gray-800 mb-4'>Certificado ou Diploma</h3>

                    {certificate.certificateURL && !certificate.file ? (
                      <div className="mb-4 p-3 border border-green-300 rounded-lg bg-green-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm text-green-700 font-medium">
                              Certificado enviado
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={certificate.certificateURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
                            >
                              Ver
                            </a>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <FileUpload
                      id={`cert-file-${certificate.id}`}
                      accept="image/*,application/pdf"
                      onFileChange={(file) => handleCertificateChange(certificate.id, 'file', file)}
                      showPreview={false}
                      allowedTypes={['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']}
                      placeholder={{
                        title: certificate.certificateURL && !certificate.file ? "Clique para alterar certificado" : "Clique para enviar certificado",
                        subtitle: "PDF, PNG, JPG ou JPEG (MAX. 5MB)"
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveCertificateRow(certificate.id)}
                    className="text-red-500 hover:text-red-700 mt-2"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>

            {Object.keys(certificates).length > 0 && (
              <div className="flex items-center justify-end mb-3">
                <button
                  type="button"
                  className="px-3 py-1 text-blue-600 border border-blue-300 rounded text-sm hover:bg-blue-50 flex items-center gap-1"
                  onClick={handleAddCertificateRow}
                >
                  <Plus className="w-3 h-3" />Adicionar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 border-t ">
          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <Spinner size="sm" />
                <span className="pl-3">Salvando...</span>
              </div>
            ) : (
              "Salvar Perfil"
            )}
          </button>

          {formMessage && (
            <HelperText className={`mt-2 text-center text-md ${formMessage.startsWith('Erro') ? 'text-red-600' : 'text-green-600'
              }`}>
              {formMessage}
            </HelperText>
          )}
        </div>
      </form>
    </div>
  );
}

export default TherapistProfile;